import type { ShopperExperience, ShopperProducts } from '@/scapi';

export const INSIDE_SPACE_CONTENT_FOLDER_ID = 'inside-space';
export const MEGA_MENU_TRENDING_CONTENT_LIMIT = 9;
export const TRENDING_NOW_COLUMN_TITLE = 'Trending Now';
export const INSIDE_SPACE_COLUMN_TITLE = 'Inside Space';
export const DEFAULT_TRENDING_CATEGORY_ATTRIBUTE = 'c_isDefaultTrendingCategory';

export type MegaMenuContentLink = {
    id: string;
    label: string;
    to: string;
};

export type MegaMenuTrendingContent = {
    categories: ShopperProducts.schemas['Category'][];
    contentLinks: MegaMenuContentLink[];
};

function getCustomAttribute(record: Record<string, unknown>, key: string): unknown {
    return record[key];
}

export function isDefaultTrendingCategory(category: ShopperProducts.schemas['Category']): boolean {
    const value = getCustomAttribute(category as Record<string, unknown>, DEFAULT_TRENDING_CATEGORY_ATTRIBUTE);

    if (value === true) {
        return true;
    }

    return typeof value === 'string' && value.trim().toLowerCase() === 'yes';
}

export function flattenCategories(
    categories: ShopperProducts.schemas['Category'][]
): ShopperProducts.schemas['Category'][] {
    return categories.flatMap((category) => [
        ...(category.id === 'root' ? [] : [category]),
        ...flattenCategories(category.categories ?? []),
    ]);
}

export function getDefaultTrendingCategories(
    categories: ShopperProducts.schemas['Category'][]
): ShopperProducts.schemas['Category'][] {
    const seen = new Set<string>();

    return flattenCategories(categories).filter((category) => {
        if (!isDefaultTrendingCategory(category) || seen.has(category.id)) {
            return false;
        }

        seen.add(category.id);
        return true;
    });
}

function isContentAsset(value: unknown): value is ShopperExperience.schemas['Content'] {
    return Boolean(value && typeof value === 'object' && typeof (value as { id?: unknown }).id === 'string');
}

export function extractContentAssetsFromFolder(
    folder: ShopperExperience.schemas['ContentFolder'] | null | undefined
): ShopperExperience.schemas['Content'][] {
    if (!folder) {
        return [];
    }

    const record = folder as Record<string, unknown>;
    const candidates = [record.content, record.contents, record.contentAssets, record.assets];

    for (const candidate of candidates) {
        if (Array.isArray(candidate)) {
            return candidate.filter(isContentAsset);
        }
    }

    return [];
}

export function toMegaMenuContentLink(asset: ShopperExperience.schemas['Content']): MegaMenuContentLink | undefined {
    const label = typeof asset.name === 'string' ? asset.name.trim() : '';
    if (!asset.id || !label) {
        return undefined;
    }

    const customPath = getCustomAttribute(asset as Record<string, unknown>, 'c_path');
    const path = typeof customPath === 'string' ? customPath.trim() : '';
    const to = path ? (path.startsWith('/') ? path : `/${path}`) : `/inside-space/${asset.id}`;

    return { id: asset.id, label, to };
}

export function getMegaMenuContentLinks(
    assets: ShopperExperience.schemas['Content'][],
    limit = MEGA_MENU_TRENDING_CONTENT_LIMIT
): MegaMenuContentLink[] {
    return assets
        .slice(0, limit)
        .map(toMegaMenuContentLink)
        .filter((link): link is MegaMenuContentLink => Boolean(link));
}
